import { useRef } from 'react';
import { useInView } from '../../hooks/useInView';

interface ServiceItem {
  icon: string;
  title: string;
  description: string;
  image: string;
}

interface ServicesProps {
  eyebrow?: string;
  headline: string;
  services: ServiceItem[];
  ctaText?: string;
  ctaHref?: string;
}

export default function SectionServices({
  eyebrow = 'Our Services',
  headline,
  services,
  ctaText = 'Explore All Diving Services',
  ctaHref = '#contact',
}: ServicesProps) {
  const { ref, inView } = useInView();

  const featured = services[0];
  const rest = services.slice(1, 4);
  const extra = services.slice(4);

  return (
    <section id="services" className="bg-[#F9F7F4] py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div ref={ref as React.RefObject<HTMLDivElement>} className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#00B4D8]" />
              <span className="text-[#00B4D8] text-[11px] font-bold uppercase tracking-[0.25em]">
                {eyebrow}
              </span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#0D1B2A] leading-[1.1] max-w-2xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {headline}
            </h2>
          </div>
          <a
            href={ctaHref}
            className="inline-flex items-center gap-2 border border-[#0D1B2A]/30 hover:border-[#00B4D8] text-[#0D1B2A] hover:text-[#00B4D8] font-semibold px-7 py-3.5 rounded-full transition-all duration-300 text-[13px] whitespace-nowrap cursor-pointer self-start lg:self-auto shrink-0"
          >
            {ctaText}
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        {/* Primary grid: featured + right column */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-5">
          {/* Featured card */}
          {featured && (
            <div className="lg:col-span-7 bg-[#0A1628] rounded-2xl overflow-hidden group cursor-pointer relative">
              <div className="w-full h-72 lg:h-80 overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/90 via-[#0A1628]/30 to-transparent lg:h-80" style={{ maxHeight: '320px' }} />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#00B4D8]/20 border border-[#00B4D8]/40 shrink-0">
                    <i className={`${featured.icon} text-[#00B4D8] text-base`} />
                  </div>
                  <span className="text-[#00B4D8] text-[10px] font-bold uppercase tracking-[0.2em]">Featured</span>
                </div>
                <h3
                  className="text-xl font-bold text-white mb-2 leading-snug"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {featured.title}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed mb-5">
                  {featured.description}
                </p>
                <div className="inline-flex items-center gap-1.5 text-[#00B4D8] text-sm font-semibold group-hover:gap-3 transition-all duration-300">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line text-base" />
                </div>
              </div>
            </div>
          )}

          {/* Right column: 2 stacked cards */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            {rest.slice(0, 2).map((svc) => (
              <div
                key={svc.title}
                className="flex-1 bg-white rounded-2xl overflow-hidden group cursor-pointer flex flex-col sm:flex-row lg:flex-col xl:flex-row hover:-translate-y-0.5 transition-transform duration-300"
              >
                <div className="sm:w-36 lg:w-full xl:w-36 sm:h-full h-40 lg:h-36 xl:h-full overflow-hidden shrink-0">
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 flex flex-col justify-center">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#E8F8FC] mb-2.5 shrink-0">
                    <i className={`${svc.icon} text-[#00B4D8] text-sm`} />
                  </div>
                  <h3
                    className="text-[15px] font-bold text-[#0D1B2A] mb-1.5 leading-snug"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {svc.title}
                  </h3>
                  <p className="text-[#687585] text-xs leading-relaxed line-clamp-3">{svc.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rest of services: 3rd item + extras */}
        {(rest.length >= 3 || extra.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...rest.slice(2), ...extra].map((svc) => (
              <div
                key={svc.title}
                className="bg-white rounded-2xl overflow-hidden group cursor-pointer hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-full h-44 overflow-hidden">
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#E8F8FC] mb-3">
                    <i className={`${svc.icon} text-[#00B4D8] text-base`} />
                  </div>
                  <h3
                    className="text-base font-bold text-[#0D1B2A] mb-2 leading-snug"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {svc.title}
                  </h3>
                  <p className="text-[#687585] text-sm leading-relaxed mb-4">{svc.description}</p>
                  <div className="inline-flex items-center gap-1 text-[#00B4D8] text-sm font-semibold group-hover:gap-2 transition-all duration-200">
                    <span>Learn More</span>
                    <i className="ri-arrow-right-line" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
